"use client";
import { Badge, Flex, Text } from '@radix-ui/themes';
import { Eye, Loader2, Save } from 'lucide-react';

interface ActionButtonsProps {
  status: string;
  seoComplete: boolean;
  checklistComplete?: boolean;
  isSaving: boolean;
  showPreview: boolean;
  onTogglePreview: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  publishLabel?: string;
}

export default function ActionButtons({
  status,
  seoComplete,
  checklistComplete = true,
  isSaving,
  showPreview,
  onTogglePreview,
  onSaveDraft,
  onPublish,
  publishLabel = 'Publish'
}: ActionButtonsProps) {
  return (
    <div
      className="flex items-center justify-between rounded-2xl shadow-sm p-5 bg-white/60 backdrop-blur-md"
    >
      <Flex align="center" gap="4">
        <Flex align="center" gap="2">
          <Text size="2" weight="medium" className="text-gray-600">
            Status:
          </Text>
          <Badge
            variant="soft"
            color={
              status === 'Published' ? 'green' :
                status === 'Scheduled' ? 'blue' : 'yellow'
            }
          >
            {status}
          </Badge>
        </Flex>
        <Flex align="center" gap="2">
          <Text size="2" weight="medium" className="text-gray-600">
            SEO:
          </Text>
          <Badge variant="soft" color={seoComplete ? 'green' : 'red'}>
            {seoComplete ? 'Complete ✓' : 'Incomplete ⚠️'}
          </Badge>
        </Flex>
      </Flex>
      <Flex align="center" gap="3">
        <button
          onClick={onTogglePreview}
          className="inline-flex items-center gap-2 rounded-lg border-2 border-sky-200 px-4 py-2.5 text-sm font-semibold text-sky-700 shadow-sm transition-all duration-200 hover:bg-sky-50 hover:border-sky-300 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed bg-white"
          disabled={isSaving}
        >
          <Eye className="h-4 w-4" />
          {showPreview ? 'Edit' : 'Preview'}
        </button>
        <button
          onClick={onSaveDraft}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg border-2 border-sky-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-sky-50 hover:border-sky-300 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>Save Draft</span>
        </button>
        <button
          onClick={onPublish}
          disabled={isSaving}
          className={`inline-flex items-center gap-2 rounded-lg border-2 border-sky-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
            !seoComplete || !checklistComplete
              ? 'bg-gradient-to-r from-sky-400 to-blue-500 opacity-75'
              : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700'
          }`}
          title={
            !seoComplete
              ? "Complete all SEO settings before publishing"
              : !checklistComplete
                ? "Some checklist items are incomplete. You can still publish, but validation will check for required fields."
                : ""
          }
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
          <span>{publishLabel}</span>
        </button>
      </Flex>
    </div>
  );
}
